import { Space } from 'antd';
import { useFormikContext } from 'formik';
import React, { useEffect, useMemo } from 'react';
import { type UploadFileFormDataFragment } from '../../../../api/fragments/UploadFileFormData';
import { type UploadFileWithPreviewFormDataFragment } from '../../../../api/fragments/UploadFileWithPreviewFormData';
import {
    type CompanySettings,
    CompanyTheme,
    type EdmEmailFooterPayload,
    type EdmEmailSocialMediaPayload,
    EmailProvider,
    MaskDirection,
    MfaSettingsType,
    SmsProvider,
} from '../../../../api/types';
import getTimeZoneByCountryCode from '../../../../utilities/getTimeZoneByCountryCode';
import CompanyLanguageSelectedProvider from '../CompanyLanguageSelectedProvider';
import useDialCodeByCountry from '../useDialCodeByCountry';
import CompanyCountryForm from './CompanyCountryForm';
import CompanyDetailsForm from './CompanyDetailsForm';
import CompanyEdmEmailFooter from './CompanyEdmEmailFooter';
import CompanySetupForm from './CompanySetupForm';

export type CompanyFormProps = {
    updated?: string;
    offset?: string;
};

const CompanyForm = ({ updated, offset }: CompanyFormProps) => {
    const { initialValues, values, setFieldValue } = useFormikContext<CompanyFormValues>();
    const { countryCode: initialCountryCode } = initialValues;
    const { countryCode } = values;
    const dialCodeByCountry = useDialCodeByCountry();

    const hasCountryCodeChanged = useMemo(() => countryCode !== initialCountryCode, [countryCode, initialCountryCode]);

    useEffect(() => {
        if (hasCountryCodeChanged) {
            const timeZone = getTimeZoneByCountryCode(countryCode);
            if (timeZone) {
                setFieldValue('timeZone', timeZone);
            }

            const dialCode = dialCodeByCountry(countryCode);
            if (dialCode) {
                setFieldValue('phone.prefix', dialCode);
            }
        }
    }, [hasCountryCodeChanged, countryCode, setFieldValue, dialCodeByCountry]);

    return (
        <CompanyLanguageSelectedProvider>
            <Space direction="vertical" style={{ width: '100%' }}>
                <CompanySetupForm offset={offset} updated={updated} />
                <CompanyCountryForm />
                <CompanyDetailsForm />
                <CompanyEdmEmailFooter />
            </Space>
        </CompanyLanguageSelectedProvider>
    );
};

export default CompanyForm;

export { default as useCompanyFormValidator } from './useCompanyFormValidator';

export type EdmSocialMediaType = EdmEmailSocialMediaPayload & {
    icon?: File | UploadFileWithPreviewFormDataFragment;
    id?: string;
};

export type CompanyFormValues = Omit<CompanySettings, 'mfaSettings' | 'edmEmailFooter'> & {
    logo?: File | UploadFileWithPreviewFormDataFragment;
    favicon?: File | UploadFileWithPreviewFormDataFragment;
    font?: File | UploadFileFormDataFragment;
    fontBold?: File | UploadFileFormDataFragment;
    mfaSettings: {
        type: MfaSettingsType;
        enabled: boolean;
    };
    edmEmailFooter: EdmEmailFooterPayload & {
        socialMedia: Array<EdmSocialMediaType>;
        emailIcon?: File | UploadFileWithPreviewFormDataFragment;
        arrowIcon?: File | UploadFileWithPreviewFormDataFragment;
        phoneIcon?: File | UploadFileWithPreviewFormDataFragment;
    };
};

export const defaultSocialMediaValue: EdmSocialMediaType = { altText: '', url: '', icon: null };

export const defaultValues: CompanyFormValues = {
    displayName: '',
    companyName: {
        defaultValue: '',
        overrides: [],
    },
    legalName: {
        defaultValue: '',
        overrides: [],
    },
    countryCode: 'SG',
    coe: 0,
    ppsr: 0,
    estFee: 0,
    timeZone: 'Asia/Singapore',
    address: '',
    roundings: {
        percentage: { decimals: 0 },
        amount: { decimals: 0 },
    },
    vatRateSettings: {
        vatRateTable: [{ startDate: new Date(), value: 0 }],
        appliedVATRate: 0, // 0% is the default VAT rate
    },
    phone: { value: '', prefix: 65 },
    languages: [],
    isActive: true,
    emailSettings: { provider: EmailProvider.System },
    smsSettings: { provider: SmsProvider.System },
    currency: '',
    copyright: {
        defaultValue: '',
        overrides: [],
    },
    color: 'ef2c72',
    theme: CompanyTheme.Default,
    email: '',
    description: {
        defaultValue: '',
        overrides: [],
    },
    sessionTimeout: 5,
    passwordConfiguration: null,
    mfaSettings: {
        enabled: false,
        type: MfaSettingsType.SmsOtp,
    },
    edmEmailFooter: {
        connectText: { defaultValue: '', overrides: [] },
        copyRight: { defaultValue: '', overrides: [] },
        disclaimerText: { defaultValue: '', overrides: [] },
        socialMedia: [defaultSocialMediaValue],
    },
    dataMask: {
        count: 0,
        direction: MaskDirection.None,
    },
    isDataPurgeEnabled: false,
    enableContentRefinement: false,
    isInstantApprovalStatsEnabled: false,
};
